// The locale a voices.js identifier carries ("…ko-KR.Yuna" → "ko-KR"). Chrome
// entries are bare names ("Yuna") and carry none.
const localeOf = (voiceURI) => {
  const m = /([a-z]{2,3})[-_]([A-Z]{2}|\d{3})/.exec(String(voiceURI || ''))
  return m ? `${m[1]}-${m[2]}` : ''
}
const normLang = (l) => String(l || '').replace('_', '-').toLowerCase()

// Resolve the voice to use, and the language to stamp on the utterance:
//   1. the exact identifier from voices.js (the Mac);
//   2. a voice of that name (Chrome lists voices by name);
//   3. any voice for that locale, then any for that language — iPadOS names
//      Apple voices differently and may only have the "enhanced" Yuna, so an
//      exact match fails there;
//   4. no voice at all, but the locale — the engine then picks its own.
// Without step 4, Korean text with no voice went to the default English voice,
// which on iOS is silence.
export function pickVoice(voiceURI) {
  const list = ('speechSynthesis' in window) ? speechSynthesis.getVoices() : []
  const exact = list.find((v) => v.voiceURI === voiceURI)
  if (exact) return { voice: exact, lang: exact.lang }
  const byName = list.find((v) => v.name === voiceURI)
  if (byName) return { voice: byName, lang: byName.lang }
  const loc = localeOf(voiceURI)
  if (!loc) return { voice: null, lang: '' }
  const sameLocale = list.find((v) => normLang(v.lang) === loc.toLowerCase())
  const sameLang = list.find((v) => normLang(v.lang).startsWith(loc.slice(0, 2).toLowerCase() + '-'))
  return { voice: sameLocale || sameLang || null, lang: loc }
}

export function speak(text, voiceURI, voiceSpeed) {
  if (!('speechSynthesis' in window) || !text) return
  const { voice, lang } = pickVoice(voiceURI)
  // Chrome's entries are bare names with no locale, and its list loads late:
  // when we know neither voice nor language, wait once for the list. Apple
  // identifiers carry the locale, so they never wait — on iOS the list is
  // often empty until something has spoken, and waiting left taps silent.
  if (!voice && !lang && speechSynthesis.getVoices().length === 0) {
    speechSynthesis.addEventListener('voiceschanged', () => speak(text, voiceURI, voiceSpeed), { once: true })
    return
  }
  const utterance = new SpeechSynthesisUtterance(text)
  if (voice) utterance.voice = voice
  if (lang) utterance.lang = lang
  // Guard against an undefined/NaN speed: setting utterance.rate to a
  // non-number makes some browsers play back at a broken, very fast rate.
  utterance.rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 1
  speechSynthesis.speak(utterance)
}

// Stop whatever is currently being spoken.
export function stopSpeaking() {
  if ('speechSynthesis' in window) speechSynthesis.cancel()
}

// Keep a reference to the live utterance: Chrome garbage-collects utterances
// mid-speech otherwise, and their events silently stop firing.
let currentUtterance = null

// Speak with progress callbacks so a view can highlight the word being read.
//   onBoundary({ charIndex, charLength, estimated }) — per word where the
//   browser reports boundaries. Where it never does (Safari, many CJK voices)
//   a timer estimates the position from characters-per-second × rate, so the
//   highlight still moves along the text. `cps` tunes that estimate (Latin
//   script ≈ 13, Korean / Japanese / Chinese ≈ 4.5).
// Returns { cancel, promise } — the promise resolves when speech ends, which
// lets a "read all" chain sentences one after another.
export function speakTracked(text, voiceURI, voiceSpeed, { onStart, onBoundary, onEnd, cps } = {}) {
  let resolveDone
  const promise = new Promise((r) => { resolveDone = r })
  if (!('speechSynthesis' in window) || !text) {
    if (onEnd) onEnd()
    resolveDone()
    return { cancel() {}, promise }
  }
  const rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 1
  let timer = null
  let gotBoundary = false
  let ended = false
  const finish = () => {
    if (ended) return
    ended = true
    if (timer) clearInterval(timer)
    if (onEnd) onEnd()
    resolveDone()
  }
  const start = () => {
    const utterance = new SpeechSynthesisUtterance(text)
    const { voice, lang } = pickVoice(voiceURI)
    if (voice) utterance.voice = voice
    if (lang) utterance.lang = lang
    utterance.rate = rate
    utterance.onstart = () => {
      if (onStart) onStart()
      const t0 = performance.now()
      const charsPerSec = (cps || 12) * rate
      timer = setInterval(() => {
        if (gotBoundary || ended) { clearInterval(timer); return }
        const idx = Math.min(text.length - 1, Math.floor(((performance.now() - t0) / 1000) * charsPerSec))
        if (onBoundary) onBoundary({ charIndex: idx, estimated: true })
      }, 90)
    }
    utterance.onboundary = (e) => {
      if (e.name && e.name !== 'word') return
      gotBoundary = true
      if (timer) clearInterval(timer)
      if (onBoundary) onBoundary({ charIndex: e.charIndex || 0, charLength: e.charLength, estimated: false })
    }
    utterance.onend = finish
    utterance.onerror = finish
    currentUtterance = utterance
    // One voice at a time. iOS Safari sometimes swallows an utterance queued in
    // the same tick as cancel(), so give it a beat when something was playing.
    if (speechSynthesis.speaking || speechSynthesis.pending) {
      speechSynthesis.cancel()
      setTimeout(() => speechSynthesis.speak(utterance), 60)
    } else {
      speechSynthesis.speak(utterance)
    }
  }
  // Start now when we know a voice or at least a language (Apple identifiers
  // carry the locale); wait once for the list only when we know neither, which
  // is Chrome's bare-name entries before its list has loaded (see speak()).
  const probe = pickVoice(voiceURI)
  if (!probe.voice && !probe.lang && speechSynthesis.getVoices().length === 0) {
    speechSynthesis.addEventListener('voiceschanged', start, { once: true })
  } else {
    start()
  }
  // Only cancel the engine if THIS utterance is still the live one — otherwise
  // an unmounting component would cut off whatever started speaking after it.
  return { cancel: () => { if (!ended) { speechSynthesis.cancel(); finish() } }, promise }
}
